/* eslint-disable max-len */
import React, { Component } from 'react';
import { compose, bindActionCreators } from 'redux';
import {
  BrowserRouter as Router,
  Switch,
  Route as DefaultRoute,
} from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { GlobalStyles, ALL_SCOPES } from '@playbookux/playbook-web-core';

// eslint-disable-next-line import/no-unresolved
import useStore from 'playbook-web/store';

import { setCoupon, fetchCompany } from '../core/actions';

import NotFound from '../core/pages/not-found';
import StyleGuidePage from '../core/pages/style-guide';

import Account from './pages/account';
import Dashboard from './pages/dashboard';
import ForgotPassword from '../core/components/forgot-password';
import HighlightReel from './pages/highlight-reel';
import ViewHighlightReel from './pages/highlight-reel/view-highlight-reel';
import CreateHighlightReel from './pages/highlight-reel/create-highlight-reel';
import LinkedInCallback from './pages/linkedin-callback';
import WorkOSCallback from '../core/components/workos-callback';
import PrivateRoute from '../core/components/private-route';
import Route from '../core/components/route';
import ProjectAnalytics from './pages/project/analytics';
import ProjectAnalyticsCardSort from './pages/project/analytics-card-sort';
import ProjectAnalyticsTreeTest from './pages/project/analytics-tree-test';
import ProjectAnalyticsSurvey from './pages/project/analytics-survey';
import ProjectParticipants from './pages/project/participants';
import ProjectParticipantsResponse from './pages/participants-response';
import ProjectPreviewStudy from './pages/project/preview-study';
import ProjectSchedule from './pages/project/schedule';
import ProjectStudy from './pages/project/study';
import ParticipantVideo from './pages/participants-video';
import QuestionVideo from './pages/question-video';
import ResetPassword from '../core/components/reset-password';
import SignIn from '../core/components/sign-in';
import SignInSSO from '../core/components/sign-in-sso';
import SignUp from './pages/sign-up';
import CreateWorkspace from './pages/create-workspace';
import ProjectEditQuestions from './pages/project/edit-questions';
import ProjectEditScreener from './pages/project/edit-screener';
import ScreenerVersions from './pages/project/screener-versions';
import SharedProject from './pages/shared-project';
import InviteUser from './pages/invite-user';
import Clip from './pages/clip';
import Tags from './pages/tags';
import ManageTags from './pages/manage-tags';
import Search from './pages/search';
import ManageFolders from './pages/manage-folders';
import TemplateLibrary from './pages/template-library';
import CreateTemplate from './pages/template-library/create-template-page';
import CreateAudience from './pages/template-library/create-audience';
import EditTemplate from './pages/template-library/edit-template-page';
import EditAudience from './pages/template-library/edit-audience';
import TemplatePreviewStudy from './pages/template-library/preview-study';
import Report from './pages/project/report';
import ReportEdit from './pages/project/report-edit';
import ReportView from './pages/project/report-view';
import SurveyTester from '../tester/pages/survey/index';
import ModeratedLivestream from './pages/moderated-livestream';

import SharedReport from './pages/shared-report';
import CreateConsentForm from './pages/template-library/consent/create-consent-form';
import RoutesPreview from './routes-preview';
import CreateProjectV2 from './pages/create-project-v2';
import RecruitLinkV2 from './pages/create-project-v2/recruit-participant-link';
import { config } from '../config';
import Integrations from './pages/integrations';
import { MaintenancePage } from '../core/pages/maintenance';
import HealthCheck from '../core/components/health-check';
import ViewAttachment from './pages/attachment/view-attachment';

class RoutesCompany extends Component {
  componentDidMount() {
    const { coupon } = queryString.parse(window.location.search);

    this.props.fetchCompany(this.props.companyId || null);

    if (coupon) {
      this.props.setCoupon(coupon);
      useStore.setState({ coupon });
    }
  }

  render() {
    return (
      <>
        <GlobalStyles />
        <Router history={this.props.history}>
          <Switch>
            {config.REACT_APP_SHOW_STYLE_GUIDE === 'true' && (
              <Route
                exact
                path="/style-guide"
                component={StyleGuidePage}
                isPublicRoute
              />
            )}
            <Route exact path="/healthcheck" component={HealthCheck} isPublicRoute />
            <Route
              exact
              path="/maintenance"
              component={MaintenancePage}
              isPublicRoute
            />
            <Route
              exact
              path="/linkedin/callback"
              component={LinkedInCallback}
            />
            <Route
              exact
              path="/workos/callback"
              component={WorkOSCallback}
            />
            <Route exact path="/sign-in" component={SignIn} />
            <Route exact path="/sign-up" component={SignUp} />
            <Route exact path="/sign-in-sso" component={SignInSSO} />
            <Route
              exact
              path="/forgot-password"
              component={ForgotPassword}
            />
            <Route
              exact
              path="/reset-password"
              component={ResetPassword}
            />

            <Route
              exact
              path="/share/:shareType/:shareCode"
              component={SharedProject}
              isPublicRoute
            />

            <Route
              exact
              path="/livestream/:testSessionId"
              component={ModeratedLivestream}
              isPublicRoute
            />

            <PrivateRoute exact path="/" component={Dashboard} />

            <PrivateRoute
              exact
              path="/create-workspace"
              component={CreateWorkspace}
            />
            <PrivateRoute exact path="/account" component={Account} />
            <PrivateRoute exact path="/manage-tags" component={ManageTags} />
            <PrivateRoute exact path="/integrations" component={Integrations} />

            <PrivateRoute exact path="/search" component={Search} />
            <PrivateRoute
              exact
              path="/manage-folders"
              component={ManageFolders}
            />

            <PrivateRoute
              exact
              path="/template-library"
              component={TemplateLibrary}
            />
            <PrivateRoute
              exact
              path="/template-library/create-questions"
              component={CreateTemplate}
            />
            <PrivateRoute
              exact
              path="/template-library/create-audience"
              component={CreateAudience}
            />
            <PrivateRoute
              exact
              path="/template-library/consent-form"
              component={CreateConsentForm}
            />
            <PrivateRoute
              exact
              path="/template-library/:templateId/edit-questions"
              component={EditTemplate}
            />
            <PrivateRoute
              exact
              path="/template-library/:templateId/edit-audience"
              component={EditAudience}
            />
            <PrivateRoute
              exact
              path="/highlight-reel"
              component={HighlightReel}
            />
            <PrivateRoute
              exact
              path="/highlight-reel/create"
              component={CreateHighlightReel}
            />
            <PrivateRoute
              exact
              path="/highlight-reel/:highlightReelId"
              component={ViewHighlightReel}
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/highlight-reel/:highlightReelId/edit"
              component={CreateHighlightReel}
            />

            <PrivateRoute exact path="/tags" component={Tags} />

            <PrivateRoute
              exact
              path="/create-project-v2/project-set-up"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/project-set-up"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/upload-details"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/details"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/audience"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/build-study"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/survey"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/summary"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/cards"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/tasks"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/tree-test"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/:projectId/instructions"
              component={CreateProjectV2}
            />

            <PrivateRoute
              exact
              path="/create-project-v2/recruit-link"
              component={RecruitLinkV2}
            />

            <PrivateRoute
              exact
              path="/projects/:projectId/study"
              component={ProjectStudy}
              active="study"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/schedule"
              component={ProjectSchedule}
              active="schedule"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/participants"
              component={ProjectParticipants}
              active="participants"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/participants-response/:testSessionId"
              component={ProjectParticipantsResponse}
              active="participants-response"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/analytics"
              component={ProjectAnalytics}
              active="analytics"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/analytics-card-sort"
              component={ProjectAnalyticsCardSort}
              active="analytics-card-sort"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/analytics-tree-test"
              component={ProjectAnalyticsTreeTest}
              active="analytics-tree-test"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/analytics-survey"
              component={ProjectAnalyticsSurvey}
              active="analytics-survey"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/videos/:testerId"
              component={ParticipantVideo}
              active="video"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/videos/questions/:questionId"
              component={QuestionVideo}
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/questions"
              component={ProjectEditQuestions}
              active="editQuestions"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/segment/:segmentId/screener"
              component={ProjectEditScreener}
              active="editScreener"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/segment/:segmentId/screeners"
              component={ScreenerVersions}
              active="screenerHistory"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/preview-study"
              component={ProjectPreviewStudy}
            />
            <PrivateRoute
              exact
              path="/templates/preview-study"
              component={TemplatePreviewStudy}
            />
            <PrivateRoute
              exact
              path="/survey"
              component={SurveyTester}
            />

            <PrivateRoute
              exact
              path="/clips/:clipId"
              component={Clip}
              scopes={ALL_SCOPES}
            />

            <PrivateRoute
              exact
              path="/projects/:projectId/reports"
              component={Report}
              active="reports"
              scopes={ALL_SCOPES}
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/reports/:reportId/edit"
              component={ReportEdit}
              active="reports"
            />
            <PrivateRoute
              exact
              path="/projects/:projectId/reports/:reportId/view"
              component={ReportView}
              active="reports"
              scopes={ALL_SCOPES}
            />

            <PrivateRoute
              exact
              path="/report/:reportId"
              component={SharedReport}
              scopes={ALL_SCOPES}
            />

            <PrivateRoute
              exact
              path="/invite/:inviteLink"
              component={InviteUser}
              isInvite
            />

            <PrivateRoute
              exact
              path="/projects/:projectId/attachments/:attachmentId"
              component={ViewAttachment}
              scopes={ALL_SCOPES}
            />

            {/* Routes of preview pages */}
            {RoutesPreview.map((previewRoute) => previewRoute)}

            <DefaultRoute render={() => <NotFound />} />
          </Switch>
        </Router>
      </>
    );
  }
}

RoutesCompany.defaultProps = {
  companyId: null,
};

RoutesCompany.propTypes = {
  setCoupon: PropTypes.func.isRequired,
  fetchCompany: PropTypes.func.isRequired,
  history: PropTypes.shape().isRequired,
  companyId: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    setCoupon,
    fetchCompany,
  },
  dispatch,
);

const mapStateToProps = (state) => ({
  companyId: state.authentication.company?.id,
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(RoutesCompany);
